import type React from 'react';
import { Fragment, useId, useState } from 'react';
import RichTextDisplay from '@components/ui/RichTextDisplay';
import type { PollRead } from '@models/api/poll';
import { pollLeaderIds, pollShare } from './pollFormat';
import { pollOwnPickIds, type PollRowState } from './pollDisplay';
import { PollOptionMark, PollOptionRow, type PollOptionControlProps } from './PollOptionRow';

export interface PollQuestionBlockProps {
  /** The feed card or the composer (FE-2b reuses the block with its own option controls). */
  mode: 'card' | 'composer';
  /**
   * What a failed answer does (TECH-197 § Design system): a standard poll reverts the pick and shows
   * the error toast (E10); a live poll keeps the tick and offers "try again" (TECH-867 E2). Answering
   * arrives with FE-1b; the block carries the mode so both tickets share one component.
   */
  onFailure: 'revert' | 'keep-and-retry';
  poll: PollRead;
  rows: PollRowState;
  /** Drawn above the question, 4px apart (the card's top line). */
  topLine?: React.ReactNode;
  /** The question: plain text, or what the card wraps it in (its opener button). */
  question: React.ReactNode;
  /** The question's heading: h2 in a feed or the sheet; h1 where it is the page (QuestionDetails). */
  headingLevel?: 1 | 2;
  /** The optional context under the question. */
  context?: string | null;
  /**
   * An interactive control per option (the composer's, FE-2b), drawn outside the part hidden from
   * screen readers. Without it each option shows the drawn mark (`PollOptionMark`).
   */
  renderOptionControl?: (props: PollOptionControlProps) => React.ReactNode;
  /**
   * The viewer can answer here (FE-1b): the options become a radio group (Pick one) or a checkbox
   * group (Pick one or more), each row the control; `onToggle` gets the tapped option.
   */
  onToggleOption?: (optionId: string) => void;
  /** E17: the options at half opacity (the viewer cannot answer here). */
  dimmed?: boolean;
  /** Drawn under an option, inside the list (an `<li>`): TECH-867 E2's "Didn't send. Tap to try again". */
  afterOption?: (optionId: string) => React.ReactNode;
}

const ARROW_STEP: Record<string, number> = {
  ArrowDown: 1,
  ArrowRight: 1,
  ArrowUp: -1,
  ArrowLeft: -1,
};

/**
 * Arrows move focus between a radio group's options (wrapping), Home and End to the ends. Focus
 * only: answering is a tap, Space or Enter, so walking the options never sends an answer each step
 * (a choice from the ticket's custom radio group; Radix's cannot be unchecked).
 */
const moveRadioFocus = (event: React.KeyboardEvent<HTMLUListElement>) => {
  const radios = [...event.currentTarget.querySelectorAll<HTMLButtonElement>('[data-poll-answer]')];
  const at = radios.indexOf(document.activeElement as HTMLButtonElement);
  if (at === -1) return;
  let next: number | null = null;
  if (event.key in ARROW_STEP) next = (at + ARROW_STEP[event.key] + radios.length) % radios.length;
  else if (event.key === 'Home') next = 0;
  else if (event.key === 'End') next = radios.length - 1;
  if (next === null) return;
  event.preventDefault();
  radios[next]?.focus();
};

/**
 * The reusable poll block (TECH-197 § Design system "PollQuestionBlock"): question, optional context
 * and the options, used by the card here and by the composer (FE-2b) and the live card (TECH-867).
 */
export const PollQuestionBlock: React.FC<PollQuestionBlockProps> = ({
  mode,
  onFailure,
  poll,
  rows,
  topLine,
  question,
  headingLevel = 2,
  context,
  renderOptionControl,
  onToggleOption,
  dimmed = false,
  afterOption,
}) => {
  const Heading = headingLevel === 1 ? 'h1' : 'h2';
  const headingId = useId();
  const leaders = rows === 'result-closed' ? pollLeaderIds(poll) : new Set<string>();
  const chosenIds = pollOwnPickIds(poll);
  // Answering: only an open poll (closed rows read as results, B3).
  const toggle = poll.is_closed ? undefined : onToggleOption;
  const answering = toggle !== undefined;
  const multiple = poll.allows_multiple;
  const rovingId =
    poll.options.find(option => chosenIds.has(option.id))?.id ?? poll.options.at(0)?.id ?? null;

  // F3: the bars grow in when results first show on this card (right after answering), not when a
  // card mounts with them (a feed load). Kept from the previous render, the pattern React documents.
  const [previousRows, setPreviousRows] = useState(rows);
  const [growIn, setGrowIn] = useState(false);
  if (previousRows !== rows) {
    setPreviousRows(rows);
    setGrowIn(previousRows === 'live' && rows === 'result-open');
  }

  return (
    <div className="flex flex-col gap-3" data-mode={mode} data-failure-mode={onFailure}>
      <div className="flex flex-col gap-1">
        {topLine}
        <Heading
          id={headingId}
          dir="auto"
          className="text-base leading-5 font-semibold text-black wrap-break-word"
        >
          {question}
        </Heading>
        {context ? (
          <RichTextDisplay
            content={context}
            className="text-sm leading-[22px] text-black"
            collapseNewlines={true}
            maxLines={mode === 'card' ? 3 : undefined}
            plainTextOnly={true}
          />
        ) : null}
      </div>
      <ul
        className={dimmed ? 'flex flex-col gap-2 opacity-50' : 'flex flex-col gap-2'}
        {...(answering
          ? {
              role: multiple ? 'group' : 'radiogroup',
              'aria-labelledby': headingId,
              onKeyDown: multiple ? undefined : moveRadioFocus,
            }
          : {})}
      >
        {poll.options.map(option => {
          const chosen = chosenIds.has(option.id);
          const controlProps: PollOptionControlProps = { option, state: rows, multiple, chosen };
          return (
            <Fragment key={option.id}>
              <PollOptionRow
                option={option}
                state={rows}
                chosen={chosen}
                leader={leaders.has(option.id)}
                share={pollShare(option.votes_count, poll.voters_count)}
                growIn={growIn}
                mark={renderOptionControl ? undefined : <PollOptionMark {...controlProps} />}
                control={renderOptionControl?.(controlProps)}
                answer={
                  toggle
                    ? {
                        role: multiple ? 'checkbox' : 'radio',
                        tabIndex: multiple || option.id === rovingId ? 0 : -1,
                        onToggle: () => {
                          toggle(option.id);
                        },
                      }
                    : undefined
                }
              />
              {afterOption?.(option.id)}
            </Fragment>
          );
        })}
      </ul>
    </div>
  );
};
